import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataGrid, GridToolbar, GridToolbarContainer, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import colorConfigs from "../../configs/colorConfigs";
import { styled, ThemeProvider, createTheme } from "@mui/material/styles";

// Create a custom theme to override MUI blue colors globally
const createCustomTheme = () => {
  return createTheme({
    palette: {
      primary: {
        main: colorConfigs.primary,
      },
      secondary: {
        main: colorConfigs.primary,
      },
      action: {
        active: colorConfigs.primary,
        hover: `${colorConfigs.primary}20`,
        selected: `${colorConfigs.primary}30`,
        focus: `${colorConfigs.primary}40`,
      },
    },
    components: {
      MuiTouchRipple: {
        styleOverrides: {
          root: {
            color: colorConfigs.primary,
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            '&.MuiPickersDay-root.Mui-selected': {
              backgroundColor: colorConfigs.primary,
            },
          },
        },
      },
    },
  });
};

const columns = [
  { field: "date", headerName: "Date", flex: 1, sortable: true },
  {
    field: "parsedStartTimeString",
    headerName: "Start Time",
    sortable: true,
    flex: 1,
  },
  {
    field: "notes",
    headerName: "Notes",
    sortable: false,
    flex: 1,
    renderCell: (params) => {
      return params.row.clinician_notes || params.row.notes || "";
    }
  },
  { 
    field: "deviceLocation", 
    headerName: "Leg", 
    sortable: false, 
    flex: 1,
    valueGetter: (params) => {
      if (params.row.deviceFirmwareVersion) {
        const side = params.row.deviceFirmwareVersion.trim();
        return side.charAt(0).toUpperCase() + side.slice(1).toLowerCase();
      }
      return 'N/A';
    }
  },
  { field: "sessionType", headerName: "Type", sortable: false, flex: 1 },
  {
    field: "durationFormatted",
    headerName: "Duration",
    sortable: true,
    flex: 1,
  },
];

// Styled refresh button to match brand colors
const StyledRefreshButton = styled(Button)(({ theme }) => ({
  borderColor: colorConfigs.primary,
  color: colorConfigs.primary,
  '&:hover': {
    backgroundColor: '#D9EEF0',
    borderColor: colorConfigs.primary,
  },
}));

const CustomToolbar = ({ onRefresh }) => {
  return (
    <GridToolbarContainer sx={{ padding: "8px 4px" }}>
      <GridToolbar 
        sx={{ 
          '& .MuiButtonBase-root': {
            color: colorConfigs.primary,
          },
          '& .MuiIconButton-root': {
            color: colorConfigs.primary,
          },
        }} 
      />
      <GridToolbarQuickFilter sx={{ 
        ml: 2,
        '& .MuiInputBase-root': { 
          borderRadius: 1.5,
          borderColor: `${colorConfigs.primary} !important`,
          '&:hover, &:focus, &.Mui-focused': {
            borderColor: `${colorConfigs.primary} !important`,
            boxShadow: `0 0 0 1px ${colorConfigs.primary}10`,
          },
        },
        '& .MuiSvgIcon-root': {
          color: colorConfigs.primary,
        },
        '& .MuiInputBase-input::placeholder': {
          color: `${colorConfigs.primary}80`,
        },
      }}/>
      <Box sx={{ flexGrow: 1 }} />
      <StyledRefreshButton
        variant="outlined"
        onClick={onRefresh}
        startIcon={<RefreshIcon />}
        size="small"
      >
        Refresh
      </StyledRefreshButton>
    </GridToolbarContainer>
  );
};

const DataGridDemo = ({ data, handleRefresh }) => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const customTheme = React.useMemo(() => createCustomTheme(), []);

  const handleRowClick = (trialId) => {
    navigate(`/dashboard/${patientId}/analyze/${trialId}`);
  }

  const processedData = React.useMemo(() => {
    return (data || []).map(trial => {
      let parsedStartTimeString = 'N/A';
      let parsedEndTimeString = 'N/A';
      let fullStartDateTime = null;
      let fullEndDateTime = null;
      let durationFormatted = 'N/A';
      let durationTotalSeconds = null;

      if (trial.timePeriod && typeof trial.timePeriod === 'string') {
        const parts = trial.timePeriod.split(' - ');
        if (parts[0]) {
          parsedStartTimeString = parts[0];
        }
        if (parts[1]) {
          parsedEndTimeString = parts[1];
        }
      }

      if (trial.date && parsedStartTimeString !== 'N/A') {
        const datePart = new Date(trial.date);
        if (!isNaN(datePart.getTime())) {
          const [hours, minutes, seconds] = parsedStartTimeString.split(':').map(Number);
          if (![hours, minutes, seconds].some(isNaN)) {
            fullStartDateTime = new Date(datePart.getFullYear(), datePart.getMonth(), datePart.getDate(), hours, minutes, seconds);
          }
        }
      }

      if (trial.date && parsedEndTimeString !== 'N/A') {
         const datePart = new Date(trial.date);
         if(!isNaN(datePart.getTime())){
            const [hours, minutes, seconds] = parsedEndTimeString.split(':').map(Number);
            if (![hours, minutes, seconds].some(isNaN)) {
              fullEndDateTime = new Date(datePart.getFullYear(), datePart.getMonth(), datePart.getDate(), hours, minutes, seconds);
            }
         }
      }
      
      if (!fullStartDateTime && trial.startTimestamp) {
          const st = new Date(trial.startTimestamp);
          if (!isNaN(st.getTime())) {
            fullStartDateTime = st;
            if (parsedStartTimeString === 'N/A') {
              parsedStartTimeString = st.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            }
          }
      }
      if (!fullEndDateTime && trial.endTimestamp) {
          const et = new Date(trial.endTimestamp);
          if (!isNaN(et.getTime())) fullEndDateTime = et;
      }

      if (fullStartDateTime && fullEndDateTime && fullEndDateTime.getTime() > fullStartDateTime.getTime()) {
        const durationMs = fullEndDateTime.getTime() - fullStartDateTime.getTime();
        durationTotalSeconds = Math.round(durationMs / 1000);
        const minutes = Math.floor(durationTotalSeconds / 60);
        const seconds = durationTotalSeconds % 60;
        if (minutes > 0) {
          durationFormatted = `${minutes}min ${seconds}s`;
        } else if (seconds > 0) {
          durationFormatted = `${seconds}s`;
        } else {
            durationFormatted = '0s';
        }
      }
      
      return {
        ...trial,
        id: trial.id, 
        parsedStartTimeString,
        durationFormatted, 
        durationTotalSeconds,
        fullStartDateTime: fullStartDateTime ? fullStartDateTime.toISOString() : null,
      };
    });
  }, [data]);

  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ 
        height: 'calc(100vh - 270px)', 
        width: '100%',
        '& .MuiDataGrid-root': {
          border: '1px solid #f0f0f0',
          borderRadius: 2,
          overflow: 'hidden',
        },
        '& .MuiDataGrid-cell': {
          borderBottom: '1px solid #f0f0f0',
          padding: '8px 16px',
        },
        '& .MuiDataGrid-columnHeaders': {
          backgroundColor: colorConfigs.primary,
          borderBottom: '1px solid #e5e9eb',
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 430,
          fontSize: '16px',
          color: colorConfigs.secondary,
          width: '100%',
          display: 'flex',
          paddingLeft: '5px',
          boxSizing: 'border-box',
        },
        '& .MuiDataGrid-footerContainer': {
          borderTop: 'none',
          backgroundColor: '#FAFAFA',
        },
        '& .MuiPaginationItem-root.Mui-selected': {
          backgroundColor: `${colorConfigs.primary}40 !important`,
          color: colorConfigs.primary,
        },
        '& .MuiDataGrid-menuIconButton': {
          color: colorConfigs.primary,
        },
        '& .MuiDataGrid-sortIcon': {
          color: colorConfigs.primary,
        },
        '& .MuiDataGrid-menuIcon': {
          color: colorConfigs.primary,
        },
        '& .MuiDataGrid-iconButtonContainer': {
          color: colorConfigs.primary,
        },
        '& .MuiDataGrid-columnSeparator': {
          display: 'none',
        },
        '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
          color: colorConfigs.primary,
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: `${colorConfigs.primary} !important`,
        },
        '& .MuiCheckbox-root.Mui-checked': {
          color: `${colorConfigs.primary} !important`,
        },
        '& .MuiButton-contained': {
          backgroundColor: `${colorConfigs.primary} !important`,
        },
        '& .MuiButton-text': {
          color: `${colorConfigs.primary} !important`,
        },
        '& .MuiSwitch-root .Mui-checked': {
          color: `${colorConfigs.primary} !important`,
        },
        '& .MuiSwitch-root .Mui-checked + .MuiSwitch-track': {
          backgroundColor: `${colorConfigs.primary} !important`,
        },
        '& .MuiRadio-root.Mui-checked': {
          color: `${colorConfigs.primary} !important`,
        },
        '& .MuiInput-underline:after': {
          borderBottomColor: `${colorConfigs.primary} !important`,
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderBottomColor: `${colorConfigs.primary} !important`,
        },
        '& .MuiFilledInput-underline:after': {
          borderBottomColor: `${colorConfigs.primary} !important`,
        },
        '& .Mui-focused': {
          color: `${colorConfigs.primary} !important`,
        },
        '& .MuiTabs-indicator': {
          backgroundColor: `${colorConfigs.primary} !important`,
        },
        '& .MuiTab-textColorPrimary.Mui-selected': {
          color: `${colorConfigs.primary} !important`,
        },
        '& .MuiPickersDay-keyboardSelected': {
          backgroundColor: `${colorConfigs.primary} !important`,
        },
        '& .MuiSelect-icon': {
          color: colorConfigs.primary,
        },
        '& .MuiLink-root': {
          color: `${colorConfigs.primary} !important`,
        },
        '& .MuiChip-clickable:hover': {
          backgroundColor: `${colorConfigs.primary}20 !important`,
        },
        '& .MuiChip-clickable:focus': {
          backgroundColor: `${colorConfigs.primary}30 !important`,
        },
      }}>
        <DataGrid
          rows={processedData}
          columns={columns}
          getRowId={(row) => row.id}
          onRowClick={(param) => handleRowClick(param.row.id)}
          sx={{
            '& .MuiDataGrid-cell:hover': {
              color: colorConfigs.primary,
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: `${colorConfigs.primary}10`,
            },
            '& .MuiDataGrid-row:nth-of-type(even)': {
              backgroundColor: '#FBFBFB',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: `2px solid ${colorConfigs.primary} !important`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: '2px 0',
            },
            '& .MuiButton-root': {
              textTransform: 'none',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: `${colorConfigs.primary}20 !important`,
            },
            '& .MuiDataGrid-row.Mui-selected:hover': {
              backgroundColor: `${colorConfigs.primary}30 !important`,
            },
            '& .MuiTablePagination-root': {
              color: colorConfigs.primary,
            },
          }}
          rowHeight={48}
          headerHeight={48}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
            sorting: {
              sortModel: [{ field: 'fullStartDateTime', sort: 'desc' }], 
            },
          }}
          pageSizeOptions={[10, 15, 20]}
          disableRowSelectionOnClick
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              onRefresh: handleRefresh,
            },
            panel: {
              sx: {
                '& .MuiTypography-root': {
                  color: colorConfigs.primary,
                },
                '& .MuiDataGrid-filterForm': {
                  '& .MuiButton-root': {
                    color: colorConfigs.primary,
                  },
                  '& .MuiFormControl-root': {
                    '& .MuiInputBase-root.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: `${colorConfigs.primary} !important`,
                      },
                    },
                  },
                  '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: `${colorConfigs.primary}20 !important`,
                    '&:hover': {
                      backgroundColor: `${colorConfigs.primary}30 !important`,
                    },
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: `${colorConfigs.primary} !important`,
                  },
                },
                '& .MuiPaper-root': {
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      color: colorConfigs.primary,
                    },
                  },
                },
              },
            },
            pagination: {
              sx: {
                '& .MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    color: colorConfigs.primary,
                  },
                },
                '& .MuiTablePagination-select': {
                  '&:focus': {
                    backgroundColor: `${colorConfigs.primary}20`,
                  },
                },
                '& .MuiTablePagination-selectIcon': {
                  color: colorConfigs.primary,
                },
              },
            },
          }}
        />
      </Box>
    </ThemeProvider>
  );
}

export default DataGridDemo;